import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Minus, Plus, ShoppingCart, Star, Package, Ruler, ChevronDown, ChevronUp, Truck, Eye, ZoomIn, X, Heart, Share2, Copy, Check } from 'lucide-react';
import useCartStore from '../store/cartStore';
import useWishlistStore from '../store/wishlistStore';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Thumbs, Zoom } from 'swiper/modules';
import { toast } from 'sonner';
import SizeChartModal from '../components/SizeChartModal';
import StickyCheckoutBar from '../components/StickyCheckoutBar';
import SimilarProducts from '../components/SimilarProducts';
import PincodeChecker from '../components/PincodeChecker';
import ProductTrustBadges from '../components/ProductTrustBadges';
import ProductReviews from '../components/ProductReviews';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category-specific size options
const SIZE_OPTIONS = {
  'shirts': ['S', 'M', 'L', 'XL', 'XXL'],
  't-shirts': ['S', 'M', 'L', 'XL', 'XXL'],
  'polo': ['S', 'M', 'L', 'XL', 'XXL'],
  'big size shirts': ['3XL', '4XL', '5XL', '6XL'],
  'plus size': ['3XL', '4XL', '5XL', '6XL'],
  'plus size collection': ['3XL', '4XL', '5XL', '6XL'],
  'jeans': ['28', '30', '32', '34', '36'],
  'big size jeans': ['38', '40', '42', '44', '46', '48'],
  'plus size jeans': ['38', '40', '42', '44', '46', '48'],
  'footwear': ['UK 3', 'UK 4', 'UK 5', 'UK 6', 'UK 7', 'UK 8', 'UK 9', 'UK 10', 'UK 11'],
  'shoes': ['UK 3', 'UK 4', 'UK 5', 'UK 6', 'UK 7', 'UK 8', 'UK 9', 'UK 10', 'UK 11'],
  'accessories': ['Free Size'],
  'perfume': ['Free Size'],
  'attar': ['Free Size'],
  'watches': ['Free Size'],
  'default': ['S', 'M', 'L', 'XL', 'XXL']
};

// Get valid sizes for a category
const getValidSizesForCategory = (categoryName) => {
  if (!categoryName) return SIZE_OPTIONS.default;
  const lowerName = categoryName.toLowerCase();
  
  // Check for plus size / big size first (most specific)
  if (lowerName.includes('plus size') || lowerName.includes('big size')) {
    if (lowerName.includes('jeans')) return SIZE_OPTIONS['big size jeans'];
    return SIZE_OPTIONS['plus size'];
  }
  
  // Check for specific categories
  for (const [key, sizes] of Object.entries(SIZE_OPTIONS)) {
    if (lowerName.includes(key)) return sizes;
  }
  
  return SIZE_OPTIONS.default;
};

// Simulate viewer count for urgency
const getViewerCount = () => Math.floor(Math.random() * 15) + 8;

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [expandedSection, setExpandedSection] = useState('description');
  const [zoomedImage, setZoomedImage] = useState(null);
  const [viewerCount] = useState(getViewerCount);
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const addItem = useCartStore(state => state.addItem);
  const { isInWishlist, toggleItem } = useWishlistStore();
  const inWishlist = product ? isInWishlist(product.id) : false;

  useEffect(() => {
    loadProduct();
    loadReviews();
    window.scrollTo(0, 0);
  }, [id]);

  const loadProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      const productData = response.data;
      setProduct(productData);
      
      // Get valid sizes for this category
      const validSizes = getValidSizesForCategory(productData.category_name);
      
      if (productData.variants && productData.variants.length > 0) {
        // Filter variants to only include valid sizes for this category
        const validVariants = productData.variants.filter(v => validSizes.includes(v.size));
        
        if (validVariants.length > 0) {
          const firstAvailable = validVariants.find(v => v.stock > 0) || validVariants[0];
          setSelectedSize(firstAvailable.size);
          setSelectedColor(firstAvailable.color);
          setSelectedVariant(firstAvailable);
        } else {
          // Fallback: use first valid size from category
          setSelectedSize(validSizes[0]);
          setSelectedColor('Standard');
        }
      } else if (productData.sizes?.length > 0) {
        // Filter product sizes to only valid ones
        const filteredSizes = productData.sizes.filter(s => validSizes.includes(s));
        setSelectedSize(filteredSizes.length > 0 ? filteredSizes[0] : validSizes[0]);
        setSelectedColor('Standard');
      } else {
        // Default to first valid size for category
        setSelectedSize(validSizes[0]);
        setSelectedColor('Standard');
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const getAvailableSizes = () => {
    // Get valid sizes for this product's category
    const validSizes = getValidSizesForCategory(product?.category_name);
    
    if (product?.variants && product.variants.length > 0) {
      // Filter variants to only show sizes that match the category
      const variantSizes = [...new Set(product.variants.map(v => v.size))];
      // Filter to only include sizes valid for this category
      const filteredSizes = variantSizes.filter(size => validSizes.includes(size));
      // If no valid sizes found, return the valid sizes for category
      return filteredSizes.length > 0 ? filteredSizes : validSizes;
    }
    
    // Fallback to product sizes if available, filtered by valid category sizes
    if (product?.sizes?.length > 0) {
      const filteredSizes = product.sizes.filter(size => validSizes.includes(size));
      return filteredSizes.length > 0 ? filteredSizes : validSizes;
    }
    
    return validSizes;
  };

  const getAvailableColors = () => {
    if (product?.variants && product.variants.length > 0) {
      // Get valid sizes for this category
      const validSizes = getValidSizesForCategory(product?.category_name);
      
      if (selectedSize && validSizes.includes(selectedSize)) {
        return [...new Set(product.variants
          .filter(v => v.size === selectedSize)
          .map(v => v.color))];
      }
      
      // Filter variants by valid sizes for this category
      const validVariants = product.variants.filter(v => validSizes.includes(v.size));
      if (validVariants.length > 0) {
        return [...new Set(validVariants.map(v => v.color))];
      }
      
      return ['Standard'];
    }
    return ['Standard'];
  };

  const handleSizeChange = (size) => {
    setSelectedSize(size);
    
    if (product?.variants && product.variants.length > 0) {
      const variantsForSize = product.variants.filter(v => v.size === size);
      if (variantsForSize.length > 0) {
        const matchingVariant = variantsForSize.find(v => v.color === selectedColor) || 
                               variantsForSize.find(v => v.stock > 0) || 
                               variantsForSize[0];
        setSelectedColor(matchingVariant.color);
        setSelectedVariant(matchingVariant);
      }
    }
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
    
    if (product?.variants && product.variants.length > 0) {
      const variant = product.variants.find(v => 
        v.size === selectedSize && v.color === color
      );
      setSelectedVariant(variant);
    }
  };

  const getStockStatus = () => {
    if (selectedVariant) {
      return selectedVariant.stock;
    }
    return product?.in_stock ? 100 : 0;
  };

  const getVariantStock = (size, color) => {
    if (product?.variants && product.variants.length > 0) {
      const variant = product.variants.find(v => v.size === size && v.color === color);
      return variant ? variant.stock : 0;
    }
    return 0;
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }
    if (!selectedColor) {
      toast.error('Please select a color');
      return;
    }
    
    const stock = getStockStatus();
    if (stock <= 0) {
      toast.error('This variant is out of stock');
      return;
    }

    if (quantity > stock) {
      toast.error(`Only ${stock} items available`);
      return;
    }
    
    addItem(product, selectedSize, selectedColor, selectedVariant?.id, quantity);
    toast.success('Added to cart!');
  };

  const handleBuyNow = () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }
    if (!selectedColor) {
      toast.error('Please select a color');
      return;
    }
    
    const stock = getStockStatus();
    if (stock <= 0) {
      toast.error('This variant is out of stock');
      return;
    }

    if (quantity > stock) {
      toast.error(`Only ${stock} items available`);
      return;
    }
    
    addItem(product, selectedSize, selectedColor, selectedVariant?.id, quantity);
    navigate('/cart');
  };

  // Wishlist toggle handler
  const handleWishlistToggle = () => {
    if (!product) return;
    const added = toggleItem(product);
    if (added) {
      toast.success('Added to Wishlist');
    } else {
      toast.success('Removed from Wishlist');
    }
  };

  // Share handlers
  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = product?.name || 'Check out this product';
    const shareText = `${product?.name} - ₹${product?.discount_price || product?.price} at JAT Fashion House`;
    
    // Check if native share is available
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShowShareModal(true);
        }
      }
    } else {
      setShowShareModal(true);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const shareToWhatsApp = () => {
    const text = `Check out ${product?.name} at JAT Fashion House! ₹${product?.discount_price || product?.price}\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    setShowShareModal(false);
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
    setShowShareModal(false);
  };

  const shareToTelegram = () => {
    const text = `${product?.name} - ₹${product?.discount_price || product?.price}`;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(text)}`, '_blank');
    setShowShareModal(false);
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Parse product features from description
  const parseFeatures = (description) => {
    if (!description) return [];
    // Try to extract key features
    const features = [];
    if (product?.fabric) features.push({ label: 'Fabric', value: product.fabric });
    features.push({ label: 'Fit', value: 'Regular Fit' });
    features.push({ label: 'Occasion', value: 'Casual, Daily Wear' });
    features.push({ label: 'Pattern', value: 'Solid' });
    features.push({ label: 'Wash Care', value: 'Machine Washable' });
    return features;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-ivory">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-xs tracking-[0.2em] uppercase text-charcoal-light">Loading</p>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const stock = getStockStatus();
  const discountPercent = product.discount_price 
    ? Math.round(((product.price - product.discount_price) / product.price) * 100)
    : 0;
  const features = parseFeatures(product.description);
  
  // Generate SEO meta data
  const productUrl = `https://jatfashionhouse.com/product/${product.id}`;
  const productImage = product.images?.[0] || 'https://jatfashionhouse.com/og-default.jpg';
  const productPrice = product.discount_price || product.price;
  const metaDescription = product.short_description || 
    `Buy ${product.name} at JAT Fashion House. Premium men's fashion at ₹${productPrice}. Free delivery across India.`;
  const pageTitle = product.name + ' | JAT Fashion House';
  const ogTitle = product.name + ' - ₹' + productPrice;

  return (
    <>
      {/* SEO Meta Tags for Social Sharing */}
      <Helmet
        title={pageTitle}
        meta={[
          { name: 'description', content: metaDescription },
          { property: 'og:title', content: ogTitle },
          { property: 'og:description', content: metaDescription },
          { property: 'og:image', content: productImage },
          { property: 'og:url', content: productUrl },
          { property: 'og:type', content: 'product' },
          { property: 'og:site_name', content: 'JAT Fashion House' },
          { property: 'og:locale', content: 'en_IN' },
          { property: 'product:price:amount', content: String(productPrice) },
          { property: 'product:price:currency', content: 'INR' },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: ogTitle },
          { name: 'twitter:description', content: metaDescription },
          { name: 'twitter:image', content: productImage }
        ]}
        link={[
          { rel: 'canonical', href: productUrl }
        ]}
      />
      
    <div className="min-h-screen bg-ivory pt-24 pb-24 lg:pb-0" data-testid="product-detail-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Images Section with Zoom */}
          <div className="space-y-4" data-testid="product-images">
            <Swiper
              modules={[Pagination, Thumbs, Zoom]}
              spaceBetween={10}
              pagination={{ clickable: true }}
              thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
              zoom={true}
              className="aspect-[3/4] bg-beige overflow-hidden"
            >
              {(product.images?.length > 0 ? product.images : ['https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=80']).map((image, index) => (
                <SwiperSlide key={index}>
                  <div className="swiper-zoom-container relative group cursor-zoom-in" onClick={() => setZoomedImage(image)}>
                    <img 
                      src={image} 
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=80';
                      }}
                    />
                    {/* Zoom Icon */}
                    <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="bg-ivory/90 backdrop-blur-sm p-3">
                        <ZoomIn className="w-5 h-5 text-charcoal" />
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            
            {/* Thumbnails */}
            {product.images?.length > 1 && (
              <Swiper
                onSwiper={setThumbsSwiper}
                spaceBetween={10}
                slidesPerView={4}
                watchSlidesProgress
                className="h-20"
              >
                {product.images.map((image, index) => (
                  <SwiperSlide key={index} className="cursor-pointer overflow-hidden">
                    <img 
                      src={image} 
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover border border-charcoal/10 hover:border-gold transition-colors"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=80';
                      }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>

          {/* Product Info Section */}
          <div data-testid="product-info">
            {/* Product Title with Wishlist & Share */}
            <div className="flex items-start justify-between gap-4 mb-3">
              <h1 className="text-2xl md:text-3xl font-bold text-charcoal flex-1" data-testid="product-name">
                {product.name}
              </h1>
              <div className="flex items-center gap-2">
                {/* Wishlist Button */}
                <button
                  onClick={handleWishlistToggle}
                  className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 border ${
                    inWishlist 
                      ? 'bg-red-500 border-red-500 text-white' 
                      : 'bg-white border-[#1A1A1A]/20 text-[#1A1A1A] hover:border-red-500 hover:text-red-500'
                  }`}
                  data-testid="wishlist-btn"
                  aria-label={inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
                >
                  <Heart className={`w-5 h-5 ${inWishlist ? 'fill-current' : ''}`} strokeWidth={1.5} />
                </button>
                {/* Share Button */}
                <button
                  onClick={handleShare}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 border bg-white border-[#1A1A1A]/20 text-[#1A1A1A] hover:border-[#C59746] hover:text-[#C59746]"
                  data-testid="share-btn"
                  aria-label="Share Product"
                >
                  <Share2 className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
            </div>

            {/* Price Section */}
            <div className="flex items-center gap-3 mb-4">
              {product.discount_price ? (
                <>
                  <span className="text-2xl md:text-3xl font-bold text-charcoal" data-testid="product-price">
                    ₹{product.discount_price}
                  </span>
                  <span className="text-lg line-through text-gray-400">₹{product.price}</span>
                  <span className="bg-gold text-white px-2 py-1 text-sm font-bold rounded">
                    {discountPercent}% OFF
                  </span>
                </>
              ) : (
                <span className="text-2xl md:text-3xl font-bold text-charcoal" data-testid="product-price">
                  ₹{product.price}
                </span>
              )}
            </div>

            {/* Trust Badges */}
            <ProductTrustBadges className="mb-4 pb-4 border-b border-gray-100" />

            {/* Urgency & Scarcity Indicators */}
            <div className="flex flex-wrap gap-2 mb-4">
              {/* Viewer Count */}
              <span className="inline-flex items-center gap-1.5 bg-purple-100 text-purple-700 px-3 py-1.5 text-sm font-semibold rounded-full" data-testid="viewer-count">
                <Eye className="w-4 h-4" />
                {viewerCount} people viewing this
              </span>
              
              {/* Stock Status */}
              {stock > 0 && stock <= 10 && (
                <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-3 py-1.5 text-sm font-semibold rounded-full animate-pulse" data-testid="low-stock-badge">
                  <Package className="w-4 h-4" />
                  Only {stock} left!
                </span>
              )}
              {stock === 0 && (
                <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1.5 text-sm font-semibold rounded-full" data-testid="out-of-stock-badge">
                  Out of Stock
                </span>
              )}
            </div>

            {/* Shipping Info */}
            <div className="mb-6 p-4 bg-blue-50  border border-blue-100" data-testid="shipping-info">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-900">Estimated Delivery: 3-5 business days</p>
                  <p className="text-sm text-blue-700">Free delivery on orders above ₹999</p>
                </div>
              </div>
            </div>

            {/* Product Features (Bullet Points) */}
            {features.length > 0 && (
              <div className="mb-6 p-4 bg-white  border border-gray-100">
                <h3 className="font-semibold text-charcoal mb-3">Key Features</h3>
                <ul className="space-y-2">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="w-1.5 h-1.5 bg-gold rounded-full"></span>
                      <span><strong>{feature.label}:</strong> {feature.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Size Selector */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-charcoal">Select Size</h3>
                <button
                  onClick={() => setShowSizeChart(true)}
                  className="flex items-center gap-1 text-gold text-sm font-semibold hover:underline"
                  data-testid="size-chart-button"
                >
                  <Ruler className="w-4 h-4" />
                  Size Guide
                </button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {getAvailableSizes().map((size) => {
                  const sizeVariants = product.variants?.filter(v => v.size === size) || [];
                  const totalSizeStock = sizeVariants.reduce((sum, v) => sum + v.stock, 0);
                  const isOutOfStock = totalSizeStock === 0;
                  
                  return (
                    <button
                      key={size}
                      onClick={() => handleSizeChange(size)}
                      disabled={isOutOfStock}
                      className={`min-w-[48px] px-4 py-2.5 font-semibold text-sm  transition-all ${
                        selectedSize === size
                          ? 'bg-gold text-white'
                          : isOutOfStock
                          ? 'bg-gray-100 text-gray-300 cursor-not-allowed line-through'
                          : 'bg-white border border-gray-200 text-charcoal hover:border-gold'
                      }`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selector */}
            <div className="mb-6">
              <h3 className="font-semibold text-charcoal mb-3">Select Color</h3>
              <div className="flex flex-wrap gap-2" data-testid="color-selector">
                {getAvailableColors().map((color) => {
                  const variantStock = getVariantStock(selectedSize, color);
                  const isOutOfStock = variantStock === 0;
                  
                  return (
                    <button
                      key={color}
                      onClick={() => !isOutOfStock && handleColorChange(color)}
                      disabled={isOutOfStock}
                      className={`px-4 py-2.5 font-semibold text-sm  transition-all relative ${
                        selectedColor === color
                          ? 'bg-gold text-white'
                          : isOutOfStock
                          ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                          : 'bg-white border border-gray-200 text-charcoal hover:border-gold'
                      }`}
                      data-testid={`color-${color}`}
                    >
                      {color}
                      {isOutOfStock && (
                        <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded text-[10px]">
                          OUT
                        </span>
                      )}
                      {!isOutOfStock && variantStock <= 10 && (
                        <span className="absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded text-[10px]">
                          {variantStock}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="mb-6">
              <h3 className="font-semibold text-charcoal mb-3">Quantity</h3>
              <div className="flex items-center gap-4" data-testid="quantity-selector">
                <div className="flex items-center bg-white border border-gray-200 ">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    data-testid="quantity-decrease"
                    disabled={stock === 0}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center font-bold" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(stock, quantity + 1))}
                    className="p-3 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    data-testid="quantity-increase"
                    disabled={quantity >= stock || stock === 0}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {stock > 0 && stock <= 10 && (
                  <span className="text-sm text-orange-600 font-medium">Max {stock} available</span>
                )}
              </div>
            </div>

            {/* Desktop CTA Buttons - MORE PROMINENT */}
            <div className="hidden lg:flex gap-4 mb-8">
              <button
                onClick={handleAddToCart}
                disabled={stock === 0}
                className="flex-1 flex items-center justify-center gap-2 py-4 px-6 border-2 border-gold text-gold font-bold text-lg  hover:bg-gold hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="w-5 h-5" />
                <span>{stock === 0 ? 'OUT OF STOCK' : 'ADD TO CART'}</span>
              </button>
              <button
                onClick={handleBuyNow}
                disabled={stock === 0}
                className="flex-1 py-4 px-6 bg-gold text-white font-bold text-lg  hover:bg-[#B8860B] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                data-testid="buy-now-button"
              >
                {stock === 0 ? 'UNAVAILABLE' : 'BUY NOW'}
              </button>
            </div>

            {/* Pincode Checker */}
            <PincodeChecker className="mb-6 p-4 bg-white  border border-gray-100" />

            {/* Accordion Sections */}
            <div className="space-y-2">
              {/* Product Description */}
              <div className="bg-white  border border-gray-100 overflow-hidden">
                <button
                  onClick={() => toggleSection('description')}
                  className="w-full flex items-center justify-between p-4 text-left"
                  data-testid="accordion-description"
                >
                  <span className="font-semibold text-charcoal">Product Description</span>
                  {expandedSection === 'description' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {expandedSection === 'description' && (
                  <div className="px-4 pb-4 text-gray-600 text-sm leading-relaxed">
                    {product.description}
                  </div>
                )}
              </div>

              {/* Care Instructions */}
              <div className="bg-white  border border-gray-100 overflow-hidden">
                <button
                  onClick={() => toggleSection('care')}
                  className="w-full flex items-center justify-between p-4 text-left"
                  data-testid="accordion-care"
                >
                  <span className="font-semibold text-charcoal">Care Instructions</span>
                  {expandedSection === 'care' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {expandedSection === 'care' && (
                  <div className="px-4 pb-4 text-gray-600 text-sm leading-relaxed">
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Machine wash cold with similar colors</li>
                      <li>Do not bleach</li>
                      <li>Tumble dry low</li>
                      <li>Iron on medium heat if needed</li>
                      <li>Do not dry clean</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Return & Shipping */}
              <div className="bg-white  border border-gray-100 overflow-hidden">
                <button
                  onClick={() => toggleSection('returns')}
                  className="w-full flex items-center justify-between p-4 text-left"
                  data-testid="accordion-returns"
                >
                  <span className="font-semibold text-charcoal">Return & Shipping Policy</span>
                  {expandedSection === 'returns' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {expandedSection === 'returns' && (
                  <div className="px-4 pb-4 text-gray-600 text-sm leading-relaxed">
                    <p className="mb-2"><strong>Shipping:</strong> Free delivery on orders above ₹999. Standard delivery 3-5 business days.</p>
                    <p className="mb-2"><strong>Exchange:</strong> Size/color exchange available on request.</p>
                    <p><strong>Contact:</strong> For any queries, contact us on WhatsApp: +91 93285 66392</p>
                  </div>
                )}
              </div>

              {/* Manufacturer Details */}
              <div className="bg-white  border border-gray-100 overflow-hidden">
                <button
                  onClick={() => toggleSection('manufacturer')}
                  className="w-full flex items-center justify-between p-4 text-left"
                  data-testid="accordion-manufacturer"
                >
                  <span className="font-semibold text-charcoal">Manufacturer Information</span>
                  {expandedSection === 'manufacturer' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {expandedSection === 'manufacturer' && (
                  <div className="px-4 pb-4 text-gray-600 text-sm leading-relaxed space-y-1">
                    <p><strong>Manufactured by:</strong> {product.manufacturer || 'Jat Fashion House'}</p>
                    <p><strong>Marketed by:</strong> {product.marketed_by || 'Jat Fashion House, Bhuj, Gujarat'}</p>
                    <p><strong>Country of Origin:</strong> {product.country_of_origin || 'India'}</p>
                    <p><strong>Consumer Complaints:</strong></p>
                    <p className="pl-4">Email: support@jatfashionhouse.com</p>
                    <p className="pl-4">Phone: +91 8141980608</p>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Reviews */}
            {reviews.length > 0 && (
              <div className="mt-8" data-testid="product-reviews">
                <h3 className="font-bold text-xl mb-4 text-charcoal">Customer Reviews</h3>
                <div className="space-y-3">
                  {reviews.map((review) => (
                    <div key={review.id} className="bg-white  p-4 border border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{review.user_name}</span>
                        <div className="flex gap-0.5">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Similar Products */}
        <SimilarProducts 
          categoryId={product.category_id} 
          currentProductId={product.id}
          title="You May Also Like"
        />

        {/* Customer Reviews Section */}
        <ProductReviews 
          productId={product.id} 
          productName={product.name}
        />
      </div>

      {/* Fullscreen Image Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setZoomedImage(null)}
          data-testid="image-zoom-modal"
        >
          <button 
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
            onClick={() => setZoomedImage(null)}
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img 
            src={zoomedImage} 
            alt={product.name}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            Tap anywhere to close
          </p>
        </div>
      )}

      {/* Size Chart Modal */}
      <SizeChartModal 
        isOpen={showSizeChart} 
        onClose={() => setShowSizeChart(false)}
        category={product.category_name}
        customImage={product.size_chart_image}
      />

      {/* Share Modal */}
      {showShareModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4"
          onClick={() => setShowShareModal(false)}
        >
          <div 
            className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[#1A1A1A]/10">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Share Product</h3>
                <button 
                  onClick={() => setShowShareModal(false)}
                  className="p-2 hover:bg-[#F5F5F5] rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-[#1A1A1A]/60" />
                </button>
              </div>
            </div>
            
            <div className="p-5">
              {/* Product Preview */}
              <div className="flex items-center gap-4 mb-6 pb-4 border-b border-[#1A1A1A]/10">
                <img 
                  src={product.images?.[0]} 
                  alt={product.name}
                  className="w-16 h-16 object-cover rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A] line-clamp-1">{product.name}</p>
                  <p className="text-sm text-[#C59746] font-medium">₹{(product.discount_price || product.price).toLocaleString()}</p>
                </div>
              </div>
              
              {/* Share Options */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <button 
                  onClick={shareToWhatsApp}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-[#F5F5F5] transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <span className="text-xs text-[#1A1A1A]/70">WhatsApp</span>
                </button>
                
                <button 
                  onClick={shareToFacebook}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-[#F5F5F5] transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-[#1877F2] flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </div>
                  <span className="text-xs text-[#1A1A1A]/70">Facebook</span>
                </button>
                
                <button 
                  onClick={shareToTelegram}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-[#F5F5F5] transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-[#0088cc] flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  </div>
                  <span className="text-xs text-[#1A1A1A]/70">Telegram</span>
                </button>
                
                <button 
                  onClick={copyLink}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-[#F5F5F5] transition-colors"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${linkCopied ? 'bg-green-500' : 'bg-[#1A1A1A]'}`}>
                    {linkCopied ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <Copy className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <span className="text-xs text-[#1A1A1A]/70">{linkCopied ? 'Copied!' : 'Copy Link'}</span>
                </button>
              </div>
              
              {/* Copy Link Input */}
              <div className="flex items-center gap-2 bg-[#F5F5F5] rounded-lg p-2">
                <input 
                  type="text"
                  value={window.location.href}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-[#1A1A1A]/70 outline-none px-2 truncate"
                />
                <button 
                  onClick={copyLink}
                  className="px-4 py-2 bg-[#1A1A1A] text-white text-xs tracking-wider uppercase font-medium hover:bg-[#333] transition-colors rounded"
                >
                  {linkCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Checkout Bar (Mobile Only) */}
      <StickyCheckoutBar
        product={product}
        stock={stock}
        selectedSize={selectedSize}
        selectedColor={selectedColor}
        onAddToCart={handleAddToCart}
        onBuyNow={handleBuyNow}
      />
    </div>
    </>
  );
};

export default ProductDetail;
